// Site scans: the pure parts (2026-10-15, reports/ar_scan_mode_spec_2026-10-14.md
// sections 18-19). No DOM and no imports, so Node can unit-test it
// (frontend/tests/scanSitesCore.test.ts). Used by the desktop map layer
// (siteScansMap.ts), the field app's map and the Street View-like viewer
// (scanViewer.ts).

export interface SiteFrame {
  session: string;
  idx: number;
  seq: number | null;
  t_ms: number | null;
  pos: number;
  version: string;
  width: number | null;
  height: number | null;
  pos_lat: number | null;
  pos_lon: number | null;
  pos_sigma_m: number | null;
  pos_source: string;
  heading_deg: number | null;
  heading_accuracy_deg: number | null;
  pitch_deg: number | null;
  roll_deg: number | null;
  fov_deg: number | null;
  screen_angle: number;
  received_at: string;
  blurred: { faces: number; plates: number };
}

export interface SiteVisit {
  client_uuid: string;
  kind: "project" | "site";
  project_id: string | null;
  project_name: string | null;
  site_label: string | null;
  recorder: string;
  mine: boolean;
  started_at: string;
  ended_at: string | null;
  n_frames: number;
  capture_type: "normal" | "as_built";
  capture_source: string | null;
  role: "current" | "history" | "as_built" | null;
  asset_ids: string[];
  retention: { until: string | null; rule: string };
}

export interface SiteSummary {
  id: number;
  name: string;
  lat: number;
  lon: number;
  bbox: [number, number, number, number] | null;
  n_sessions: number;
  n_frames: number;
  n_as_built?: number;
  has_as_built?: boolean;
  first_visit_at: string | null;
  last_visit_at: string | null;
  stale?: boolean;
  stale_warning?: string | null;
  last_scanned_at?: string | null;
  accuracy: { n_fixes: number; median_m: number | null; p90_m: number | null; best_m: number | null; centroid_sigma_m: number | null };
  model?: { status: string };
  distance_m?: number;
  similarity?: number | null;
  match?: boolean;
}

const D2R = Math.PI / 180;
const M_PER_DEG = 111_320;

/** Metres east/north of (lat, lon) from (lat0, lon0): small-offset equirectangular. */
export function enuOffset(lat0: number, lon0: number, lat: number, lon: number): [number, number] {
  return [(lon - lon0) * M_PER_DEG * Math.cos(lat0 * D2R), (lat - lat0) * M_PER_DEG];
}

/** The frame nearest a clicked point (a map click: "jump to nearest frame").
 * Only frames with a position; null when none. */
export function nearestFrame<T extends { pos_lat: number | null; pos_lon: number | null }>(frames: T[], lon: number, lat: number): { index: number; distance_m: number } | null {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < frames.length; i++) {
    const f = frames[i];
    if (f.pos_lat === null || f.pos_lon === null) continue;
    const [e, n] = enuOffset(lat, lon, f.pos_lat, f.pos_lon);
    const d = Math.hypot(e, n);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best < 0 ? null : { index: best, distance_m: bestD };
}

/** Step along the capture path: clamped, never wraps. */
export function stepIndex(i: number, delta: number, n: number): number {
  if (n <= 0) return 0;
  return Math.min(n - 1, Math.max(0, i + delta));
}

/** Which frames to prefetch around `i`: the next `ahead`, then the previous
 * `behind`, nearest first, within range. */
export function prefetchOrder(i: number, n: number, ahead = 3, behind = 1): number[] {
  const out: number[] = [];
  for (let k = 1; k <= Math.max(ahead, behind); k++) {
    if (k <= ahead && i + k < n) out.push(i + k);
    if (k <= behind && i - k >= 0) out.push(i - k);
  }
  return out;
}

/** A horizontal swipe's direction: +1 next (swipe left), -1 previous
 * (swipe right), 0 not a swipe (too short or too vertical). */
export function swipeStep(dx: number, dy: number, minPx = 40): -1 | 0 | 1 {
  if (Math.abs(dx) < minPx || Math.abs(dx) < 1.5 * Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}

/** The viewing cone for the mini-map: [lon, lat] ring from the frame
 * position, `fovDeg` wide about `headingDeg`, `lengthM` long. */
export function headingCone(lat: number, lon: number, headingDeg: number, fovDeg = 60, lengthM = 18, steps = 8): [number, number][] {
  const ring: [number, number][] = [[lon, lat]];
  const cos = Math.cos(lat * D2R);
  for (let k = 0; k <= steps; k++) {
    const b = (headingDeg - fovDeg / 2 + (fovDeg * k) / steps) * D2R;
    ring.push([lon + (Math.sin(b) * lengthM) / (M_PER_DEG * cos), lat + (Math.cos(b) * lengthM) / M_PER_DEG]);
  }
  ring.push([lon, lat]);
  return ring;
}

/** Small heading arrows along a capture track (higher zooms): every
 * `every`-th point with a heading, a chevron of two `sizeM` strokes
 * pointing along the heading. Each arrow is one [lon, lat] polyline. */
export function headingArrows(points: [number, number, number | null, number][], every = 2, sizeM = 1.6): [number, number][][] {
  const out: [number, number][][] = [];
  for (let i = 0; i < points.length; i += Math.max(1, every)) {
    const [lon, lat, hd] = points[i];
    if (hd === null || !Number.isFinite(hd)) continue;
    const cos = Math.cos(lat * D2R);
    const pt = (bearing: number, m: number): [number, number] => [lon + (Math.sin(bearing * D2R) * m) / (M_PER_DEG * cos), lat + (Math.cos(bearing * D2R) * m) / M_PER_DEG];
    // a chevron ahead of the dot: left wing -> tip -> right wing
    const tip = pt(hd, sizeM);
    const wing = (side: number): [number, number] => {
      const base = pt(hd, sizeM * 0.35);
      const b = (hd + side * 90) * D2R;
      return [base[0] + (Math.sin(b) * sizeM * 0.45) / (M_PER_DEG * cos), base[1] + (Math.cos(b) * sizeM * 0.45) / M_PER_DEG];
    };
    out.push([wing(-1), tip, wing(1)]);
  }
  return out;
}

export interface Cluster<T> {
  lon: number;
  lat: number;
  items: T[];
  bbox: [number, number, number, number];
}

const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * D2R) / 2)) / D2R;

/** Grid clustering of site markers at a zoom (cells `cellPx` screen pixels
 * wide). From `maxZoom` in, every site is its own cluster. */
export function clusterSites<T extends { lon: number; lat: number }>(items: T[], zoom: number, cellPx = 56, maxZoom = 15): Cluster<T>[] {
  if (zoom >= maxZoom) return items.map((s) => ({ lon: s.lon, lat: s.lat, items: [s], bbox: [s.lon, s.lat, s.lon, s.lat] }));
  const cell = (360 / 2 ** Math.floor(zoom)) * (cellPx / 256);
  const cells = new Map<string, { sx: number; sy: number; c: Cluster<T> }>();
  for (const s of items) {
    const key = `${Math.floor(s.lon / cell)}:${Math.floor(mercY(s.lat) / cell)}`;
    let e = cells.get(key);
    if (!e) {
      e = { sx: 0, sy: 0, c: { lon: s.lon, lat: s.lat, items: [], bbox: [s.lon, s.lat, s.lon, s.lat] } };
      cells.set(key, e);
    }
    e.sx += s.lon;
    e.sy += s.lat;
    e.c.items.push(s);
    const b = e.c.bbox;
    b[0] = Math.min(b[0], s.lon);
    b[1] = Math.min(b[1], s.lat);
    b[2] = Math.max(b[2], s.lon);
    b[3] = Math.max(b[3], s.lat);
  }
  return [...cells.values()].map((e) => ({ ...e.c, lon: e.sx / e.c.items.length, lat: e.sy / e.c.items.length }));
}

/** "As-built · <project> · 15 Oct 2026" / "Visit · 15 Oct 2026 · Sam". */
export function visitLabel(v: Pick<SiteVisit, "capture_type" | "project_name" | "started_at" | "recorder" | "role">): string {
  const d = shortDate(v.started_at);
  if (v.capture_type === "as_built") return ["As-built", v.project_name, d].filter(Boolean).join(" · ");
  const role = v.role === "history" ? "History" : "Visit";
  return [role, d, v.recorder].filter(Boolean).join(" · ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** A frame's W3C beta/gamma from its stored pitch/roll (heading.ts records
 * pitch = beta - 90, roll = gamma), for arMath.cameraBasis. */
export function frameBetaGamma(f: Pick<SiteFrame, "pitch_deg" | "roll_deg">): [number, number] {
  return [(f.pitch_deg ?? 0) + 90, f.roll_deg ?? 0];
}

export interface AreaAsset {
  asset_id: string;
  asset_type?: string;
  water_type?: string;
  path?: [number, number][];
  lon?: number;
  lat?: number;
}

export interface AssetLabel {
  asset_id: string;
  asset_type: string | null;
  x: number;
  y: number;
  distance_m: number;
}

/** The point of an asset to label: a register point, or the point of a
 * pipe's path nearest the camera. ENU metres from the frame position. */
export function assetAnchor(a: AreaAsset, lat0: number, lon0: number): [number, number] | null {
  if (a.lon !== undefined && a.lat !== undefined) return enuOffset(lat0, lon0, a.lat, a.lon);
  if (!a.path || a.path.length === 0) return null;
  const pts = a.path.map(([lo, la]) => enuOffset(lat0, lon0, la, lo));
  let best: [number, number] | null = null;
  let bd = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[Math.min(i + 1, pts.length - 1)];
    const dx = q[0] - p[0], dy = q[1] - p[1];
    const L = dx * dx + dy * dy;
    const t = L > 0 ? Math.max(0, Math.min(1, -(p[0] * dx + p[1] * dy) / L)) : 0;
    const c: [number, number] = [p[0] + t * dx, p[1] + t * dy];
    const d = Math.hypot(c[0], c[1]);
    if (d < bd) {
      bd = d;
      best = c;
    }
  }
  return best;
}

/** Labels for the WRAP assets near a frame, placed with the frame's stored
 * pose. `project(e, n)` is arMath.projectPoint bound to the frame's view
 * (ground point, u = 0); null = behind the camera. Within `maxM`, inside the
 * image, nearest first, at most `limit`. Positions are APPROXIMATE: phone
 * GPS + compass at capture time. */
export function assetLabels(f: Pick<SiteFrame, "pos_lat" | "pos_lon">, assets: AreaAsset[], width: number, height: number,
  project: (e: number, n: number) => [number, number] | null, maxM = 30, limit = 12): AssetLabel[] {
  if (f.pos_lat === null || f.pos_lon === null) return [];
  const out: AssetLabel[] = [];
  for (const a of assets) {
    const p = assetAnchor(a, f.pos_lat, f.pos_lon);
    if (!p) continue;
    const d = Math.hypot(p[0], p[1]);
    if (d > maxM) continue;
    const s = project(p[0], p[1]);
    if (!s || s[0] < 0 || s[0] > width || s[1] < 0 || s[1] > height) continue;
    out.push({ asset_id: a.asset_id, asset_type: a.asset_type ?? null, x: s[0], y: s[1], distance_m: d });
  }
  out.sort((a, b) => a.distance_m - b.distance_m);
  return out.slice(0, limit);
}

/** A radius (m) around a site that covers its frames' extent plus `padM`. */
export function siteRadiusM(s: Pick<SiteSummary, "lat" | "lon" | "bbox">, padM = 40): number {
  if (!s.bbox) return padM + 20;
  const [w, so, e, n] = s.bbox;
  const c = [enuOffset(s.lat, s.lon, so, w), enuOffset(s.lat, s.lon, n, e)];
  return Math.round(Math.max(...c.map(([x, y]) => Math.hypot(x, y))) + padM);
}

// --- Smooth moves between frames (row #32, 2026-10-19) -----------------------

type V3 = [number, number, number];

/** A frame's camera: centre (m) and its right / image-up / forward unit
 * vectors, in one right-handed frame with z up. From the site model's SfM
 * cameras when the frame is placed in it (model/poses), else from the phone's
 * compass + tilt (arMath.cameraBasis; centre unknown). */
export interface FramePose {
  centre: V3 | null;
  right: V3;
  up: V3;
  fwd: V3;
  /** the ground's z in the same frame (m), when known: sets the depth the move is scaled by */
  ground_z?: number | null;
}

/** How the OUTGOING picture moves while the next one fades in: a shift as
 * fractions of the shown picture's width / height (+x right, +y down) and a
 * zoom (> 1 = walking forward into it). The incoming picture starts at the
 * inverse and settles at rest. `basis`: "model" = from the SfM poses,
 * "compass" = rotation from the phone's sensors plus a nominal step. */
export interface FrameMove {
  dx: number;
  dy: number;
  zoom: number;
  yaw_deg: number;
  basis: "model" | "compass";
}

const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The depth (m) the picture's middle is at: where `a`'s optical axis meets
 * the ground when it looks down at it, else `fallbackM`, within 2-25 m. */
export function viewDepth(a: FramePose, fallbackM = 8): number {
  if (a.centre && a.ground_z !== null && a.ground_z !== undefined && a.fwd[2] < -0.05) {
    const d = (a.ground_z - a.centre[2]) / a.fwd[2];
    if (d > 0) return clamp(d, 2, 25);
  }
  return fallbackM;
}

/** The move from frame `a` to frame `b` for a picture whose focal length is
 * `fx` of its width and `fy` of its height (focal px / width px, focal px /
 * height px: arMath.focalPx, or the SfM intrinsics). With both centres known (SfM
 * poses) the translation counts: sideways as parallax at the view's depth,
 * forward as a zoom. Without them (`basis` "compass") only the turn counts,
 * plus a nominal `stepM` forward (+) or back (-) along the path. */
export function frameMove(a: FramePose, b: FramePose, fx: number, fy: number, stepM = 0): FrameMove {
  const x = dot(b.fwd, a.right), y = dot(b.fwd, a.up), z = dot(b.fwd, a.fwd);
  const yaw = Math.atan2(x, z), pitch = Math.atan2(y, Math.hypot(x, z));
  const model = !!(a.centre && b.centre);
  const t: V3 = model
    ? (() => { const d: V3 = [b.centre![0] - a.centre![0], b.centre![1] - a.centre![1], b.centre![2] - a.centre![2]]; return [dot(d, a.right), dot(d, a.up), dot(d, a.fwd)] as V3; })()
    : [0, 0, stepM];
  const D = viewDepth(a);
  const turnX = Math.tan(clamp(yaw, -1.2, 1.2)), turnY = Math.tan(clamp(pitch, -1.2, 1.2));
  return {
    dx: clamp(-fx * (turnX + t[0] / D), -1, 1),
    dy: clamp(fy * (turnY + t[1] / D), -1, 1),
    zoom: clamp(D / Math.max(D - t[2], 0.4 * D), 0.6, 1.8),
    yaw_deg: yaw / D2R,
    basis: model ? "model" : "compass",
  };
}

// --- Street View-style hotspots (row #37, 2026-10-19) ---------------------------

/** A place you can move to, drawn on the picture: frame `k` of the list, at (x, y) as fractions of the
 * picture (the ground under that frame's camera), `dist_m` away, `bearing_deg` right of straight ahead. */
export interface Hotspot {
  k: number;
  x: number;
  y: number;
  dist_m: number;
  bearing_deg: number;
}

/** The neighbouring capture points seen from camera `a`: each candidate's camera centre dropped `heightM` to
 * the ground along `up` (the world's up in the poses' frame), projected with focal fractions fx / fy (as for
 * frameMove). Only points in front, inside the picture and `minM`-`maxM` away; the nearest one per `binDeg`
 * of bearing (so one hotspot per way you can go), nearest first, at most `limit`. */
export function hotspots(a: FramePose, cands: { k: number; pose: FramePose }[], fx: number, fy: number, up: V3,
  opts: { minM?: number; maxM?: number; heightM?: number; binDeg?: number; limit?: number } = {}): Hotspot[] {
  const { minM = 1.5, maxM = 20, heightM = 1.4, binDeg = 20, limit = 5 } = opts;
  if (!a.centre) return [];
  const best = new Map<number, Hotspot>();
  for (const { k, pose } of cands) {
    const c = pose.centre;
    if (!c) continue;
    const dist = Math.hypot(c[0] - a.centre[0], c[1] - a.centre[1], c[2] - a.centre[2]);
    if (dist < minM || dist > maxM) continue;
    const g: V3 = [c[0] - heightM * up[0], c[1] - heightM * up[1], c[2] - heightM * up[2]];
    const d: V3 = [g[0] - a.centre[0], g[1] - a.centre[1], g[2] - a.centre[2]];
    const xr = dot(d, a.right), yu = dot(d, a.up), zf = dot(d, a.fwd);
    if (zf < 0.5) continue;
    const x = 0.5 + (fx * xr) / zf, y = 0.5 - (fy * yu) / zf;
    if (x < 0.04 || x > 0.96 || y < 0.05 || y > 0.97) continue;
    const bearing = (Math.atan2(xr, zf) * 180) / Math.PI;
    const bin = Math.round(bearing / binDeg);
    const prev = best.get(bin);
    if (!prev || dist < prev.dist_m) best.set(bin, { k, x, y, dist_m: dist, bearing_deg: bearing });
  }
  return [...best.values()].sort((p, q) => p.dist_m - q.dist_m).slice(0, limit);
}

/** The world's up in a set of SfM cameras' frame: the mean of their image-up vectors (phones are held
 * upright), unit length; z when there are none. */
export function meanUp(ups: V3[]): V3 {
  const s: V3 = [0, 0, 0];
  for (const u of ups) { s[0] += u[0]; s[1] += u[1]; s[2] += u[2]; }
  const n = Math.hypot(s[0], s[1], s[2]);
  return n > 1e-9 ? [s[0] / n, s[1] / n, s[2] / n] : [0, 0, 1];
}
